import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useApi, useWorkspace } from '~/contexts';
import { PageLoader } from '~/routes/public/pages';
import templates from './templates';

const ReportsContext = React.createContext();

function ReportsProvider({ children }) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const [data, setData] = useState(null);

  const fetchData = useCallback(async () => {
    const { data } = await api.www.workspaces(workspace.id).reports().list();
    setData(data);
  }, [api, workspace.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const reports =
    data
      ?.map((r) => {
        const template = templates[r.templateKey];
        if (!template) return null;
        return {
          ...r,
          section: template.section,
          component: template.component,
        };
      })
      .filter(Boolean) || [];

  const addFavorite = useCallback(
    async (reportId) => {
      await api.www.workspaces(workspace.id).reports(reportId).favorites.add();
      await fetchData();
    },
    [api, fetchData, workspace.id],
  );

  const removeFavorite = useCallback(
    async (reportId) => {
      await api.www.workspaces(workspace.id).reports(reportId).favorites.remove();
      await fetchData();
    },
    [api, fetchData, workspace.id],
  );

  const addPinned = useCallback(
    async (reportId) => {
      await api.www.workspaces(workspace.id).reports(reportId).pinned.add();
      await fetchData();
    },
    [api, fetchData, workspace.id],
  );

  const removePinned = useCallback(
    async (reportId) => {
      await api.www.workspaces(workspace.id).reports(reportId).pinned.remove();
      await fetchData();
    },
    [api, fetchData, workspace.id],
  );

  return (
    <ReportsContext.Provider value={{ reports, addFavorite, removeFavorite, addPinned, removePinned, fetchData }}>
      {data === null ? <PageLoader /> : children}
    </ReportsContext.Provider>
  );
}

function useReports() {
  const context = useContext(ReportsContext);
  return context;
}

export { ReportsContext, ReportsProvider, useReports };
